import api from '../lib/api'
import type { Hub, CreateHubRequest, UpdateHubRequest, HubInvitation, HubMember } from '../types'

export type { Hub, HubInvitation, HubConnection } from '../types';

class HubService {
  private baseUrl = '/nexus-hub/hubs'; // Updated to match Kong route prefix

  async getHubs(): Promise<Hub[]> {
    const response = await api.get(this.baseUrl);
    return response.data ?? [];
  }

  async getHub(hubId: string): Promise<Hub> {
    const response = await api.get(`${this.baseUrl}/${hubId}`);
    return response.data;
  }

  async createHub(data: CreateHubRequest): Promise<Hub> {
    const response = await api.post(this.baseUrl, data);
    return response.data;
  }

  async updateHub(hubId: string, data: UpdateHubRequest): Promise<Hub> {
    const response = await api.put(`${this.baseUrl}/${hubId}`, data);
    return response.data;
  }

  async deleteHub(hubId: string): Promise<void> {
    await api.delete(`${this.baseUrl}/${hubId}`);
  }

  // Note: These invitation endpoints may not exist in the backend yet
  // They're kept for future implementation
  async inviteMember(hubId: string, data: any): Promise<HubInvitation> {
    // Accepts: { email?: string, message?: string, usageLimit?: number, inviteMethod?: 'link' | 'email' }
    const response = await api.post(`${this.baseUrl}/${hubId}/invitations`, data);
    return response.data;
  }

  async getInvitations(): Promise<HubInvitation[]> {
    const response = await api.get(`${this.baseUrl}/invitations`);
    return response.data ?? [];
  }

  async getInvitationsForHub(hubId: string): Promise<HubInvitation[]> {
    const response = await api.get(`${this.baseUrl}/${hubId}/invitations`);
    return response.data ?? [];
  }

  async acceptInvitation(invitationToken: string): Promise<Hub> {
    const response = await api.post(`${this.baseUrl}/invitations/${invitationToken}/accept`);
    return response.data;
  }

  async declineInvitation(invitationToken: string): Promise<void> {
    await api.post(`${this.baseUrl}/invitations/${invitationToken}/decline`);
  }

  async removeMember(hubId: string, memberId: string): Promise<void> {
    await api.delete(`${this.baseUrl}/${hubId}/members/${memberId}`);
  }

  async updateMemberRole(hubId: string, memberId: string, role: 'admin' | 'member'): Promise<HubMember> {
    const response = await api.put(`${this.baseUrl}/${hubId}/members/${memberId}/role`, { role });
    return response.data;
  }

  async leaveHub(hubId: string): Promise<void> {
    await api.post(`${this.baseUrl}/${hubId}/leave`);
  }

  async getChannels(hubId: string) {
    const response = await api.get(`/nexus-hub/hubs/${hubId}/channels`);
    return response.data;
  }

  async getHubMembers(hubId: string) {
    const response = await api.get(`${this.baseUrl}/${hubId}/members`);
    return response.data;
  }

  // Get all hubs the user is a member of (owner or accepted invitee)
  async getMemberHubs(): Promise<Hub[]> {
    const response = await api.get('/nexus-hub/hubs/memberships');
    return response.data ?? [];
  }
}

export const hubService = new HubService() 